/**
 * The list view. Every row is a link to its record (click, or Enter when focused), the header
 * stays put while the body scrolls, and loading, empty and more-to-load are states of the table
 * rather than separate screens, so nothing jumps when the data arrives.
 */
import type { ReactNode } from "react";
import { useNavigate } from "react-router";

import { EmptyState, ErrorState } from "./ui";
import type { IconName } from "./icons";

export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  num?: boolean;
  width?: number | string;
}

export function DataTable<T extends { id: string }>({
  columns,
  rows,
  href,
  loading,
  error,
  onRetry,
  empty,
  hasMore,
  loadingMore,
  onMore,
  selection,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  href: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty: { icon?: IconName; title: string; body?: string };
  hasMore?: boolean;
  loadingMore?: boolean;
  onMore?: () => void;
  /** Ticks down the left for bulk actions; the row itself still opens the record. */
  selection?: { selected: Set<string>; onChange: (next: Set<string>) => void };
}) {
  const navigate = useNavigate();
  if (error) {
    return (
      <div className="table-wrap">
        <ErrorState error={error} onRetry={onRetry} />
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            {selection && (
              <th className="tick">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={
                    Boolean(rows?.length) && rows!.every((r) => selection.selected.has(r.id))
                  }
                  onChange={(e) =>
                    selection.onChange(
                      new Set(e.target.checked ? (rows ?? []).map((r) => r.id) : []),
                    )
                  }
                />
              </th>
            )}
            {columns.map((c) => (
              <th
                key={c.header}
                className={c.num ? "num" : undefined}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && !rows
            ? Array.from({ length: 8 }, (_, i) => (
                <tr key={i} className="skel-row" aria-hidden="true">
                  {selection && <td className="tick" />}
                  {columns.map((c) => (
                    <td key={c.header}>
                      <div className="skel" />
                    </td>
                  ))}
                </tr>
              ))
            : rows?.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={0}
                  onClick={(e) => {
                    // A link, button or tick inside the row keeps its own click.
                    if ((e.target as HTMLElement).closest("a, button, input, label")) return;
                    navigate(href(row));
                  }}
                  onKeyDown={(e) => e.key === "Enter" && navigate(href(row))}
                >
                  {selection && (
                    <td className="tick">
                      <input
                        type="checkbox"
                        aria-label="Select"
                        checked={selection.selected.has(row.id)}
                        onChange={(e) => {
                          const next = new Set(selection.selected);
                          if (e.target.checked) next.add(row.id);
                          else next.delete(row.id);
                          selection.onChange(next);
                        }}
                      />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.header} className={c.num ? "num" : undefined}>
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
      {rows && rows.length === 0 && (
        <EmptyState icon={empty.icon} title={empty.title}>
          {empty.body}
        </EmptyState>
      )}
      {hasMore && (
        <div className="table-foot">
          <button className="btn btn-sm" onClick={onMore} disabled={loadingMore}>
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
